"use client";

import { ArrowRight, Wind, Frame, Gauge, Activity, Zap, ChartLine as LineChart } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ApplicationsProducts() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    index: "[01]",
    title: "Phenolic Engraving",
    description: "Industrial-grade phenolic resin plates engineered for extreme durability and high-contrast legibility in harsh environments.",
    Icon: Wind
  }, {
    index: "[02]",
    title: "Arc Flash Labels",
    description: "Compliant safety labeling solutions designed to meet rigorous electrical standards and withstand long-term exposure.",
    Icon: Frame
  }, {
    index: "[03]",
    title: "Direct Part Marking",
    description: "Precision DPM services providing permanent identification directly onto metal and plastic components for traceability.",
    Icon: Gauge
  }, {
    index: "[04]",
    title: "Metal Laser Engraving",
    description: "High-precision laser marking on stainless steel, aluminum, and brass for permanent, professional-grade identification.",
    Icon: Activity
  }, {
    index: "[05]",
    title: "Custom Engraving",
    description: "Tailored engraving solutions for specialized equipment, control panels, and unique industrial identification requirements.",
    Icon: Zap
  }, {
    index: "[06]",
    title: "Submittal Packages",
    description: "Comprehensive documentation and sample sets prepared to streamline your project approval and compliance processes.",
    Icon: LineChart
  }];
  return <section data-section-id="4310" id="applications-products" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-4 flex flex-col gap-4">
            <span className="font-mono text-xs md:text-sm text-accent tracking-widest">
              // SECTION 02
            </span>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-foreground/60">
              Our Capabilities
            </span>
          </div>

          <div className="md:col-span-8">
            <h2 className="font-default font-bold uppercase text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight">
              Industrial{" "}
              <span className="block md:inline">
                <span className="font-serif italic font-normal lowercase">solutions.</span>
              </span>
            </h2>
          </div>
        </div>

        {}
        <div className="mt-12 md:mt-16 flex items-center gap-4">
          <span className="font-mono text-xs text-foreground/60 tracking-widest">A.01</span>
          <div className="flex-1 h-px bg-foreground/10" />
          <span className="font-mono text-xs text-foreground/60 tracking-widest">A.06</span>
        </div>

        {}
        <div ref={gridRef} className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, i) => {
          const Icon = service.Icon;
          const isOffset = i % 2 === 1;
          return <div data-index={i} key={i} className={`group relative bg-card border border-border shadow-lg backdrop-blur-sm p-8 md:p-10 min-h-[380px] flex flex-col transition-all duration-300 hover:bg-card/80 hover:border-accent hover:shadow-2xl ${isOffset ? "lg:mt-4" : ""} ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: gridVisible ? `${i * 80}ms` : "0ms",
            transitionProperty: "opacity, transform, background-color, border-color, box-shadow",
            transitionDuration: "500ms"
          }}>
                {}
                <div className="flex items-start justify-between">
                  <span className="font-mono text-xs tracking-widest text-foreground/50">
                    {service.index} — [06]
                  </span>
                  <Icon className="size-10 text-accent transition-all duration-300 group-hover:scale-110" strokeWidth={1.25} aria-hidden="true" />
                </div>

                {}
                <div className="h-16" />

                {}
                <h3 className="font-default font-bold uppercase text-2xl md:text-[28px] leading-tight tracking-tight">
                  {service.title}
                </h3>

                {}
                <p className="mt-5 font-default text-base leading-relaxed text-foreground/70 line-clamp-3">
                  {service.description}
                </p>

                {}
                <div className="flex-1" />

                {}
                <div className="mt-8">
                  <Link to={i === 5 ? "/ContactPage" : "/ServicesPage"} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-foreground hover:text-accent transition-colors duration-300">
                    <span>Explore</span>
                    <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true" />
                  </Link>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-20 md:mt-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-10 border-t border-border">
          <p className="font-default text-lg md:text-xl text-foreground/70 max-w-xl">
            Six core service areas. One objective: precision-engineered identification for your industrial infrastructure.
          </p>
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 font-mono uppercase tracking-[0.2em] text-xs h-12 px-8 shadow-lg">
            <Link to="/ServicesPage">
              <span>View All Services</span>
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}